// client/src/coms/views/canvas_parts/ImportedBrushImportModal.js
//
// Brush importer and library manager. Import: drop packs, review every brush
// with a rendered stroke preview and an honest compatibility rating, try it
// on a test canvas that uses the real GPU renderer, then import the
// selection. Library: search, favourites, recents, trash, rename, duplicate,
// adjust, export and back up. Everything stays in this browser.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './brushImporter.css';
import { createCanvasGpuEngine } from './canvas_room_view_parts/canvasGpuCore';
import { inspectFiles } from './canvas_room_view_parts/brushImport/brushPackClient.js';
import {
  exportRecords,
  findDuplicates,
  forgetPreparedCandidates,
  importCandidates,
  importNativeExport,
  isNativeExport,
  listRecords,
  loadRuntimePreset,
  loadRuntimeShell,
  notifyLibraryChanged,
  prepareCandidate,
  previewCandidate,
  purge,
  putRecords,
  softDelete,
  storageUsage,
  updateRecord,
  LIBRARY_SOFT_LIMIT_BYTES,
} from './canvas_room_view_parts/brushImport/brushLibrary.js';
import { COMPATIBILITY_LABELS, normaliseBrushRecord } from './canvas_room_view_parts/brushImport/brushSchema.js';
import { ACCEPTED_BRUSH_EXTENSIONS } from './canvas_room_view_parts/brushImport/formatDetect.js';
import { applyEndTaper, createStrokeState, importedStrokeSessionOptions, planDabs } from './canvas_room_view_parts/brushImport/dabPlanner.js';

const APP_LABELS = { procreate: 'Procreate', krita: 'Krita', mypaint: 'MyPaint', photoshop: 'Photoshop', gimp: 'GIMP', image: 'Image', voxemera: 'Voxemera', unknown: 'Other' };
const APP_INITIALS = { procreate: 'P', krita: 'K', mypaint: 'M', photoshop: 'Ps', gimp: 'G', image: 'I', voxemera: 'V', unknown: '?' };
const STATUS_ORDER = ['full', 'high', 'partial', 'unsupported'];
const STATUS_COLOURS = { full: 'var(--vbi-good)', high: 'var(--vbi-high)', partial: 'var(--vbi-partial)', unsupported: '#9aa3b2' };
const ACCEPT = [...ACCEPTED_BRUSH_EXTENSIONS, '.voxbrush'].join(',');

// The test surface starts blank, so erasers are tried as their shape.
function testSessionOptions(brush, options) {
  return { ...importedStrokeSessionOptions(brush, options), isEraser: false };
}

function plural(count, word, many = word + 's') {
  return `${count} ${count === 1 ? word : many}`;
}

function formatBytes(bytes) {
  if (!bytes) return '0 MB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1048576).toFixed(bytes < 10 * 1048576 ? 1 : 0)} MB`;
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function Icon({ name }) {
  const paths = {
    close: <path d="M4 4l8 8M12 4l-8 8" />,
    check: <path d="M3.5 8.5l3 3 6-7" />,
    star: <path d="M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z" />,
    upload: <path d="M8 11V2.5M4.5 6L8 2.5 11.5 6M2.5 11v2.5h11V11" />,
    trash: <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 9h5.8l.6-9" />,
    copy: <path d="M5.5 5.5h7.5v7.5H5.5zM3 10.5V3h7.5" />,
    download: <path d="M8 2.5V11M4.5 7.5L8 11l3.5-3.5M2.5 13.5h11" />,
    undo: <path d="M5.5 3L2.5 6l3 3M2.5 6h7a4 4 0 010 8H6" />,
  };
  return (
    <svg viewBox="0 0 16 16" fill={name === 'star' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function AppMark({ app }) {
  const key = APP_LABELS[app] ? app : 'unknown';
  return <span className="vbi-app" data-app={key} title={APP_LABELS[key]}>{APP_INITIALS[key]}</span>;
}

function CompatBadge({ status }) {
  return <span className="vbi-badge" data-status={status}>{COMPATIBILITY_LABELS[status] || status}</span>;
}

function DropArt() {
  return (
    <svg className="vbi-drop-art" viewBox="0 0 150 96" fill="none" aria-hidden="true">
      <rect x="14" y="10" width="122" height="76" rx="14" fill="#f7f3ea" opacity="0.95" />
      <path d="M30 62c14-22 26-28 38-18s24 10 36-8 18-12 24-6" stroke="#4f9dff" strokeWidth="9" strokeLinecap="round" opacity="0.9" />
      <path d="M32 40c10-8 20-10 30-4" stroke="#a25bff" strokeWidth="5" strokeLinecap="round" opacity="0.75" />
      <path d="M90 70c8-4 16-4 26 2" stroke="#f2b84b" strokeWidth="6" strokeLinecap="round" strokeDasharray="1 7" />
    </svg>
  );
}

// Serial queue so stroke previews render one at a time without blocking.
const previewQueue = [];
let previewBusy = false;
const previewUrls = new Map();

function requestCandidatePreview(candidate, done) {
  if (previewUrls.has(candidate.key)) {
    done(previewUrls.get(candidate.key));
    return () => {};
  }
  const job = { candidate, done, cancelled: false };
  previewQueue.push(job);
  pumpPreviews();
  return () => {
    job.cancelled = true;
  };
}

async function pumpPreviews() {
  if (previewBusy) return;
  previewBusy = true;
  while (previewQueue.length) {
    const job = previewQueue.shift();
    if (job.cancelled) continue;
    if (previewUrls.has(job.candidate.key)) {
      job.done(previewUrls.get(job.candidate.key));
      continue;
    }
    try {
      const result = await previewCandidate(job.candidate);
      const url = result.canvas ? result.canvas.toDataURL('image/png') : '';
      previewUrls.set(job.candidate.key, url);
      job.done(url);
    } catch {
      previewUrls.set(job.candidate.key, '');
      job.done('');
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  previewBusy = false;
}

function useVisible(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, visible]);
  return visible;
}

function CandidatePreview({ candidate }) {
  const ref = useRef(null);
  const visible = useVisible(ref);
  const [url, setUrl] = useState(() => previewUrls.get(candidate.key));
  const paintable = candidate.translation.compatibility.status !== 'unsupported';
  useEffect(() => {
    if (!visible || url !== undefined || !paintable) return undefined;
    return requestCandidatePreview(candidate, setUrl);
  }, [candidate, paintable, url, visible]);
  return (
    <div className="vbi-card-preview" ref={ref}>
      {!paintable ? (
        <div className="vbi-unpaintable"><span>{candidate.engineLabel} engine</span><span>Source kept · not paintable yet</span></div>
      ) : url ? <img src={url} alt="" /> : url === '' ? <div className="vbi-unpaintable">Preview unavailable</div> : <div className="vbi-skeleton" />}
    </div>
  );
}

// --- Test canvas (real GPU renderer) -----------------------------------------

function BrushTestCanvas({ brush, onReady }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const strokeRef = useRef(null);
  const [size, setSize] = useState(28);
  const [colour, setColour] = useState('#1b1d22');
  const [error, setError] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round((rect.width || 300) * ratio));
    canvas.height = Math.max(1, Math.round((rect.height || 188) * ratio));
    let engine = null;
    try {
      engine = createCanvasGpuEngine(canvas, canvas.width, canvas.height);
      engine.setTransparentPaper(true);
      engine.setActiveLayer('layer-1');
      engine.render(false);
      engineRef.current = engine;
      if (typeof onReady === 'function') onReady(true);
    } catch (err) {
      setError('WebGL is unavailable, so the test canvas cannot run.');
    }
    return () => {
      engineRef.current = null;
      if (engine) engine.destroy();
    };
  }, [onReady]);

  const point = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const pressure = event.pointerType === 'pen' ? (event.pressure > 0 ? event.pressure : 0.5) : 0.72;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY, pressure, tiltX: event.tiltX || 0, tiltY: event.tiltY || 0 };
  };

  const options = () => {
    const ratio = canvasRef.current ? canvasRef.current.width / Math.max(1, canvasRef.current.getBoundingClientRect().width) : 1;
    return { size: size * ratio, color: colour, opacity: 1, strokeSeed: Date.now() % 100000 };
  };

  const onPointerDown = (event) => {
    const engine = engineRef.current;
    if (!engine || !brush || !brush.loaded) return;
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    const opts = options();
    const state = createStrokeState({ logDabs: true });
    const start = point(event);
    engine.beginImportedStroke('layer-1', testSessionOptions(brush, opts));
    const plan = planDabs(start, null, brush, opts, state, 0, true, performance.now());
    if (plan.dabs.length) engine.drawImportedBrushDabsV2(plan.dabs, brush, { ...opts, ...testSessionOptions(brush, opts) });
    strokeRef.current = { opts, state, last: start, dabIndex: plan.dabIndex };
  };

  const onPointerMove = (event) => {
    const engine = engineRef.current;
    const stroke = strokeRef.current;
    if (!engine || !stroke) return;
    const events = typeof event.nativeEvent.getCoalescedEvents === 'function' ? event.nativeEvent.getCoalescedEvents() : [event.nativeEvent];
    const dabs = [];
    (events.length ? events : [event.nativeEvent]).forEach((native) => {
      const next = point(native);
      const plan = planDabs(stroke.last, next, brush, stroke.opts, stroke.state, stroke.dabIndex, false, performance.now());
      stroke.dabIndex = plan.dabIndex;
      stroke.last = next;
      dabs.push(...plan.dabs);
    });
    if (dabs.length) engine.drawImportedBrushDabsV2(dabs, brush, { ...stroke.opts, ...testSessionOptions(brush, stroke.opts) });
  };

  const onPointerUp = () => {
    const engine = engineRef.current;
    const stroke = strokeRef.current;
    strokeRef.current = null;
    if (!engine || !stroke) return;
    const tapered = applyEndTaper(stroke.state.dabLog, brush, stroke.opts);
    if (tapered) engine.redrawImportedStroke(tapered, brush, { ...stroke.opts, ...testSessionOptions(brush, stroke.opts) });
    engine.endRoundBrushStroke('layer-1');
    engine.render(false);
  };

  const clear = () => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.clearLayer('layer-1');
    engine.render(false);
  };

  return (
    <div className="vbi-test">
      {error ? <div className="vbi-empty" style={{ minHeight: 140 }}>{error}</div> : (
        <canvas
          ref={canvasRef}
          className="vbi-test-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-label="Brush test canvas"
        />
      )}
      <div className="vbi-test-bar">
        <span>Size</span>
        <input type="range" min="2" max="140" value={size} onChange={(event) => setSize(Number(event.target.value))} aria-label="Test brush size" />
        <span style={{ width: 26, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{size}</span>
        <input type="color" value={colour} onChange={(event) => setColour(event.target.value)} aria-label="Test colour" />
        <button type="button" className="vbi-button" data-size="small" onClick={clear}>Clear</button>
      </div>
      <div className="vbi-test-hint">{brush && brush.loaded ? 'Draw here with a pen to try pressure and tilt. Uses the same renderer as the canvas.' : 'Loading brush…'}</div>
    </div>
  );
}

function CompatibilityDetails({ compatibility, defaultOpen = true }) {
  if (!compatibility) return null;
  const { status, score, implemented = [], approximated = [], missing = [], notes = [] } = compatibility;
  return (
    <details className="vbi-section" open={defaultOpen}>
      <summary>
        Compatibility
        <CompatBadge status={status} />
      </summary>
      <div className="vbi-section-body">
        <div className="vbi-score">
          <div className="vbi-score-bar"><span style={{ width: `${score}%`, background: STATUS_COLOURS[status] }} /></div>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{score}/100</span>
        </div>
        {!compatibility.validatedAgainstReference && status !== 'unsupported' ? (
          <p style={{ margin: '8px 0 0', color: 'var(--vbi-text-3)', fontSize: 11 }}>Not yet compared against reference strokes from the original app, so this is not rated fully supported.</p>
        ) : null}
        {missing.length ? (<><h4>Not reproduced</h4><ul>{missing.map((item) => <li key={item}>{item.replace(/ \(major\)$/, '')}{/\(major\)$/.test(item) ? <strong style={{ color: 'var(--vbi-partial)' }}> · significant</strong> : null}</li>)}</ul></>) : null}
        {approximated.length ? (<><h4>Approximated</h4><ul>{approximated.map((item) => <li key={item}>{item}</li>)}</ul></>) : null}
        {implemented.length ? (<><h4>Translated</h4><ul>{implemented.map((item) => <li key={item}>{item}</li>)}</ul></>) : null}
        {notes.length ? (<><h4>Notes</h4><ul>{notes.map((item) => <li key={item}>{item}</li>)}</ul></>) : null}
      </div>
    </details>
  );
}

function SourceDetails({ source, engineLabel }) {
  if (!source) return null;
  return (
    <details className="vbi-section">
      <summary>Source</summary>
      <div className="vbi-section-body">
        <dl className="vbi-kv">
          <dt>App</dt><dd>{APP_LABELS[source.app] || source.app}</dd>
          {engineLabel || source.engine ? (<><dt>Engine</dt><dd>{engineLabel || source.engine}</dd></>) : null}
          {source.packName ? (<><dt>Pack</dt><dd>{source.packName}</dd></>) : null}
          {source.fileName ? (<><dt>File</dt><dd>{source.fileName}</dd></>) : null}
          {source.entryPath ? (<><dt>Entry</dt><dd>{source.entryPath}</dd></>) : null}
          {source.author ? (<><dt>Author</dt><dd>{source.author}</dd></>) : null}
          {source.license ? (<><dt>Licence</dt><dd>{source.license}</dd></>) : null}
        </dl>
        <p style={{ margin: '8px 0 0', color: 'var(--vbi-text-3)', fontSize: 11 }}>The original settings are stored with the brush so future versions can re-translate it.</p>
      </div>
    </details>
  );
}

// --- Brush adjustments (library) -------------------------------------------------

function Slider({ label, value, min, max, step, format, onChange }) {
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '92px 1fr 44px', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      <span style={{ color: 'var(--vbi-text-3)' }}>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} style={{ accentColor: 'var(--vbi-accent)' }} />
      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{format ? format(value) : value}</span>
    </label>
  );
}

function BrushAdjustments({ record, onChange }) {
  const pct = (value) => `${Math.round(value * 100)}%`;
  const set = (path, value) => {
    const next = JSON.parse(JSON.stringify(record));
    let cursor = next;
    for (let i = 0; i < path.length - 1; i += 1) cursor = cursor[path[i]];
    cursor[path[path.length - 1]] = value;
    onChange(next);
  };
  return (
    <details className="vbi-section">
      <summary>Adjust</summary>
      <div className="vbi-section-body">
        <Slider label="Spacing" value={record.settings.spacing} min={0.01} max={1.5} step={0.01} format={pct} onChange={(v) => set(['settings', 'spacing'], v)} />
        <Slider label="Opacity" value={record.settings.opacity} min={0.05} max={1} step={0.01} format={pct} onChange={(v) => set(['settings', 'opacity'], v)} />
        <Slider label="Flow" value={record.settings.flow} min={0.01} max={1} step={0.01} format={pct} onChange={(v) => set(['settings', 'flow'], v)} />
        <Slider label="Size pressure" value={record.dynamics.size.pressure.amount} min={0} max={1} step={0.01} format={pct} onChange={(v) => set(['dynamics', 'size', 'pressure', 'amount'], v)} />
        <Slider label="Opacity press." value={record.dynamics.opacity.pressure.amount} min={0} max={1} step={0.01} format={pct} onChange={(v) => set(['dynamics', 'opacity', 'pressure', 'amount'], v)} />
        <Slider label="Size jitter" value={record.dynamics.size.jitter} min={0} max={1} step={0.01} format={pct} onChange={(v) => set(['dynamics', 'size', 'jitter'], v)} />
        <Slider label="Scatter" value={record.dynamics.scatter.perpendicular} min={0} max={2} step={0.01} format={pct} onChange={(v) => set(['dynamics', 'scatter', 'perpendicular'], v)} />
        <Slider label="Follow stroke" value={record.dynamics.rotation.follow} min={-1} max={1} step={0.01} format={pct} onChange={(v) => set(['dynamics', 'rotation', 'follow'], v)} />
        {record.texture ? <Slider label="Grain depth" value={record.texture.depth} min={0} max={1} step={0.01} format={pct} onChange={(v) => set(['texture', 'depth'], v)} /> : null}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <input type="checkbox" checked={record.settings.opacityMode === 'stroke'} onChange={(event) => set(['settings', 'opacityMode'], event.target.checked ? 'stroke' : 'dab')} style={{ accentColor: 'var(--vbi-accent)' }} />
          Cap opacity per stroke (glaze / wash)
        </label>
      </div>
    </details>
  );
}

// --- Main component ----------------------------------------------------------

export default function ImportedBrushImportModal({
  open,
  importedBrushPresets = [],
  activeImportedBrushId = '',
  onClose,
  onAddImportedBrushPresets,
  onSetActiveImportedBrushId,
  initialFiles = null,
  onInitialFilesConsumed,
}) {
  const [tab, setTab] = useState('import');
  const [dragDepth, setDragDepth] = useState(0);
  const fileInputRef = useRef(null);
  const restoreInputRef = useRef(null);
  const abortRef = useRef(null);

  // Import state
  const [phase, setPhase] = useState('idle');
  const [candidates, setCandidates] = useState([]);
  const [inspectWarnings, setInspectWarnings] = useState([]);
  const [failures, setFailures] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [focusedKey, setFocusedKey] = useState('');
  const [folderFilter, setFolderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [duplicates, setDuplicates] = useState(() => new Map());
  const [duplicatePolicy, setDuplicatePolicy] = useState('skip');
  const [progress, setProgress] = useState(null);
  const [resultMessage, setResultMessage] = useState(null);
  const [focusedRuntime, setFocusedRuntime] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Library state
  const [records, setRecords] = useState([]);
  const [shells, setShells] = useState(() => new Map());
  const [libraryView, setLibraryView] = useState('all');
  const [libraryFolder, setLibraryFolder] = useState('');
  const [libraryApp, setLibraryApp] = useState('');
  const [search, setSearch] = useState('');
  const [librarySelected, setLibrarySelected] = useState(() => new Set());
  const [focusedId, setFocusedId] = useState('');
  const [focusedLibraryRuntime, setFocusedLibraryRuntime] = useState(null);
  const [usage, setUsage] = useState(null);
  const [nameDraft, setNameDraft] = useState('');

  const refreshLibrary = useCallback(async () => {
    try {
      const list = await listRecords();
      setRecords(list);
      const nextShells = new Map();
      for (const record of list) nextShells.set(record.id, await loadRuntimeShell(record));
      setShells(nextShells);
      setUsage(await storageUsage());
    } catch {
      setRecords([]);
    }
  }, []);

  useEffect(() => {
    if (open) refreshLibrary();
  }, [open, refreshLibrary]);

  // --- Import pipeline ---------------------------------------------------------

  const startInspection = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setTab('import');
    setResultMessage(null);
    const native = [];
    const packs = [];
    for (const file of files) {
      if (/\.voxbrush$/i.test(file.name)) native.push(file);
      else packs.push(file);
    }
    if (native.length) {
      let restored = 0;
      for (const file of native) {
        try {
          const bytes = new Uint8Array(await file.arrayBuffer());
          if (isNativeExport(bytes)) restored += (await importNativeExport(bytes)).length;
        } catch {}
      }
      notifyLibraryChanged({ reason: 'restore' });
      await refreshLibrary();
      setResultMessage({ tone: 'info', text: `Restored ${plural(restored, 'brush', 'brushes')} from Voxemera export${native.length > 1 ? 's' : ''}.` });
      if (!packs.length) {
        setTab('library');
        return;
      }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    forgetPreparedCandidates();
    setPhase('inspecting');
    setProgress({ value: 0, label: `Reading ${plural(packs.length, 'file')}…` });
    try {
      const result = await inspectFiles(packs, {
        signal: controller.signal,
        onProgress: (p) => setProgress({
          value: p.total ? (p.index + (p.phase === 'hash' ? 0.9 : 0.4)) / p.total : 0,
          label: p.phase === 'hash' ? 'Checking for duplicates…' : p.fileName ? `Inspecting ${p.fileName}…` : 'Reading files…',
        }),
      });
      const dupes = await findDuplicates(result.candidates);
      setCandidates((current) => {
        const known = new Set(current.map((item) => item.key));
        return [...current, ...result.candidates.filter((item) => !known.has(item.key))];
      });
      setDuplicates((current) => new Map([...current, ...dupes]));
      setSelected((current) => {
        const next = new Set(current);
        result.candidates.forEach((item) => {
          if (item.selectedByDefault && item.translation.compatibility.status !== 'unsupported' && !dupes.has(item.key)) next.add(item.key);
        });
        return next;
      });
      setInspectWarnings((current) => [...current, ...result.warnings].slice(0, 200));
      setFailures((current) => [...current, ...result.failures]);
      if (result.candidates[0]) setFocusedKey((key) => key || result.candidates[0].key);
      setPhase(result.candidates.length || candidates.length ? 'review' : 'idle');
    } catch (err) {
      setPhase(candidates.length ? 'review' : 'idle');
      if (err && err.name !== 'AbortError') setFailures((current) => [...current, { fileName: packs.map((f) => f.name).join(', '), message: err.message }]);
    } finally {
      setProgress(null);
      abortRef.current = null;
    }
  }, [candidates.length, refreshLibrary]);

  useEffect(() => {
    if (open && initialFiles && initialFiles.length) {
      startInspection(initialFiles);
      if (typeof onInitialFilesConsumed === 'function') onInitialFilesConsumed();
    }
  }, [initialFiles, onInitialFilesConsumed, open, startInspection]);

  const focusedCandidate = useMemo(() => candidates.find((item) => item.key === focusedKey) || null, [candidates, focusedKey]);

  useEffect(() => {
    let cancelled = false;
    setFocusedRuntime(null);
    if (!focusedCandidate || focusedCandidate.translation.compatibility.status === 'unsupported') return undefined;
    prepareCandidate(focusedCandidate).then((prepared) => {
      if (!cancelled) setFocusedRuntime(prepared.runtime);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [focusedCandidate]);

  const folders = useMemo(() => {
    const map = new Map();
    candidates.forEach((item) => {
      const name = item.folderPath || 'Brushes';
      if (!map.has(name)) map.set(name, { name, app: item.app, keys: [] });
      map.get(name).keys.push(item.key);
    });
    return Array.from(map.values());
  }, [candidates]);

  const visibleCandidates = useMemo(() => candidates.filter((item) => (
    (!folderFilter || (item.folderPath || 'Brushes') === folderFilter)
    && (!statusFilter || item.translation.compatibility.status === statusFilter)
  )), [candidates, folderFilter, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = {};
    candidates.forEach((item) => {
      const status = item.translation.compatibility.status;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [candidates]);

  const selectedCandidates = useMemo(() => candidates.filter((item) => selected.has(item.key)), [candidates, selected]);
  const selectedDuplicates = selectedCandidates.filter((item) => duplicates.has(item.key)).length;

  const toggleKeys = (keys, on) => setSelected((current) => {
    const next = new Set(current);
    keys.forEach((key) => (on ? next.add(key) : next.delete(key)));
    return next;
  });

  const runImport = useCallback(async () => {
    if (!selectedCandidates.length) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('importing');
    setProgress({ value: 0, label: 'Preparing…' });
    try {
      const { imported, failed } = await importCandidates(selectedCandidates, {
        duplicates,
        policy: () => duplicatePolicy,
        signal: controller.signal,
        onProgress: (p) => setProgress({ value: p.index / Math.max(1, p.total), label: `Importing ${p.name}` }),
      });
      if (imported.length && typeof onAddImportedBrushPresets === 'function') {
        const paintable = imported.filter((brush) => brush.paintable);
        onAddImportedBrushPresets(imported, paintable.length ? paintable[0].id : '');
        if (paintable.length && typeof onSetActiveImportedBrushId === 'function') onSetActiveImportedBrushId(paintable[0].id);
      }
      notifyLibraryChanged({ reason: 'import' });
      await refreshLibrary();
      const skipped = selectedCandidates.length - imported.length - failed.length;
      setResultMessage({
        tone: failed.length ? 'warn' : 'info',
        text: `Imported ${plural(imported.length, 'brush', 'brushes')}${skipped > 0 ? `, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}` : ''}${controller.signal.aborted ? ' before you cancelled' : ''}.`,
        failed,
      });
      const importedKeys = new Set(selectedCandidates.map((item) => item.key));
      setCandidates((current) => current.filter((item) => !importedKeys.has(item.key)));
      setSelected(new Set());
      setFocusedKey('');
      setPhase('idle');
      if (imported.length) setTab('library');
    } catch (err) {
      setResultMessage({ tone: 'warn', text: 'Import failed: ' + (err && err.message ? err.message : 'unknown error') });
      setPhase('review');
    } finally {
      setProgress(null);
      abortRef.current = null;
    }
  }, [duplicatePolicy, duplicates, onAddImportedBrushPresets, onSetActiveImportedBrushId, refreshLibrary, selectedCandidates]);

  const cancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const clearReview = () => {
    setCandidates([]);
    setSelected(new Set());
    setFocusedKey('');
    setFolderFilter('');
    setStatusFilter('');
    setInspectWarnings([]);
    setFailures([]);
    setDuplicates(new Map());
    setPhase('idle');
    forgetPreparedCandidates();
  };

  // --- Library -----------------------------------------------------------------

  const recentIds = useMemo(() => records
    .filter((record) => record.library.lastUsedAt && !record.library.deletedAt)
    .sort((a, b) => b.library.lastUsedAt - a.library.lastUsedAt)
    .slice(0, 24)
    .map((record) => record.id), [records]);

  const libraryFolders = useMemo(() => {
    const map = new Map();
    records.filter((record) => !record.library.deletedAt).forEach((record) => {
      map.set(record.library.folder, (map.get(record.library.folder) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [records]);

  const visibleRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = records.filter((record) => (libraryView === 'trash' ? record.library.deletedAt : !record.library.deletedAt));
    if (libraryView === 'favourites') list = list.filter((record) => record.library.favourite);
    if (libraryView === 'recent') list = recentIds.map((id) => list.find((record) => record.id === id)).filter(Boolean);
    if (libraryFolder) list = list.filter((record) => record.library.folder === libraryFolder);
    if (libraryApp) list = list.filter((record) => record.source.app === libraryApp);
    if (query) {
      list = list.filter((record) => [record.name, record.library.folder, record.source.app, record.source.engine, record.source.packName, APP_LABELS[record.source.app]]
        .some((value) => String(value || '').toLowerCase().includes(query)));
    }
    return list;
  }, [libraryApp, libraryFolder, libraryView, recentIds, records, search]);

  const focusedRecord = useMemo(() => records.find((record) => record.id === focusedId) || null, [focusedId, records]);

  useEffect(() => {
    let cancelled = false;
    setFocusedLibraryRuntime(null);
    setNameDraft(focusedRecord ? focusedRecord.name : '');
    if (!focusedRecord) return undefined;
    loadRuntimePreset(focusedRecord).then((runtime) => {
      if (!cancelled) setFocusedLibraryRuntime(runtime);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [focusedRecord]);

  const afterLibraryChange = useCallback(async (reason) => {
    notifyLibraryChanged({ reason });
    await refreshLibrary();
  }, [refreshLibrary]);

  const toggleFavourite = async (record) => {
    await updateRecord(record.id, { library: { favourite: !record.library.favourite } });
    afterLibraryChange('favourite');
  };

  const commitName = async () => {
    if (!focusedRecord) return;
    const name = nameDraft.trim();
    if (!name || name === focusedRecord.name) {
      setNameDraft(focusedRecord.name);
      return;
    }
    await updateRecord(focusedRecord.id, { name });
    afterLibraryChange('rename');
  };

  const duplicateRecord = async (record) => {
    const copy = normaliseBrushRecord({
      ...record,
      id: 'brush-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9),
      name: `${record.name} copy`,
      createdAt: Date.now(),
      library: { ...record.library, favourite: false, lastUsedAt: 0, order: record.library.order + 0.5 },
    });
    await putRecords([copy]);
    setFocusedId(copy.id);
    afterLibraryChange('duplicate');
  };

  const adjustRecord = async (next) => {
    const saved = await updateRecord(next.id, next);
    if (saved) {
      setRecords((current) => current.map((record) => (record.id === saved.id ? saved : record)));
      notifyLibraryChanged({ reason: 'edit', id: saved.id });
    }
  };

  const libraryIdsForAction = () => (librarySelected.size ? Array.from(librarySelected) : focusedRecord ? [focusedRecord.id] : []);

  const deleteSelected = async () => {
    const ids = libraryIdsForAction();
    if (!ids.length) return;
    await softDelete(ids, true);
    setLibrarySelected(new Set());
    if (ids.includes(activeImportedBrushId) && typeof onSetActiveImportedBrushId === 'function') onSetActiveImportedBrushId('');
    afterLibraryChange('delete');
  };

  const restoreSelected = async () => {
    const ids = libraryIdsForAction();
    if (!ids.length) return;
    await softDelete(ids, false);
    setLibrarySelected(new Set());
    afterLibraryChange('restore');
  };

  const purgeSelected = async () => {
    const ids = libraryIdsForAction();
    if (!ids.length) return;
    await purge(ids);
    setLibrarySelected(new Set());
    setFocusedId('');
    afterLibraryChange('purge');
  };

  const exportSelected = async (all = false) => {
    const list = all ? records.filter((record) => !record.library.deletedAt) : records.filter((record) => libraryIdsForAction().includes(record.id));
    if (!list.length) return;
    const blob = await exportRecords(list, { kind: all ? 'backup' : 'presets' });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, all ? `voxemera-brush-library-${stamp}.voxbrush` : `${list.length === 1 ? list[0].name.replace(/[^\w -]+/g, '').trim() || 'brush' : `voxemera-brushes-${stamp}`}.voxbrush`);
  };

  const activateBrush = (record) => {
    if (!record || typeof onSetActiveImportedBrushId !== 'function') return;
    onSetActiveImportedBrushId(record.id);
    updateRecord(record.id, { library: { lastUsedAt: Date.now() } }).then(() => notifyLibraryChanged({ reason: 'used' }));
  };

  // --- Drag and drop -------------------------------------------------------------

  const onDragEnter = (event) => {
    if (!Array.from(event.dataTransfer.types || []).includes('Files')) return;
    event.preventDefault();
    setDragDepth((depth) => depth + 1);
  };
  const onDragLeave = () => setDragDepth((depth) => Math.max(0, depth - 1));
  const onDragOver = (event) => {
    if (Array.from(event.dataTransfer.types || []).includes('Files')) event.preventDefault();
  };
  const onDrop = (event) => {
    event.preventDefault();
    setDragDepth(0);
    if (event.dataTransfer.files && event.dataTransfer.files.length) startInspection(event.dataTransfer.files);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        if (abortRef.current) abortRef.current.abort();
        else if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  if (!open) return null;

  const busy = phase === 'inspecting' || phase === 'importing';
  const libraryCount = records.filter((record) => !record.library.deletedAt).length;
  const trashCount = records.filter((record) => record.library.deletedAt).length;

  const renderImportSide = () => (
    <aside className="vbi-side" aria-label="Packs and folders">
      <h3>Packs</h3>
      <button type="button" className="vbi-folder" aria-current={!folderFilter} onClick={() => setFolderFilter('')}>
        <span className="vbi-folder-name">All brushes</span>
        <span className="vbi-folder-count">{candidates.length}</span>
      </button>
      {folders.map((folder) => {
        const chosen = folder.keys.filter((key) => selected.has(key)).length;
        return (
          <div key={folder.name} className="vbi-folder-row">
            <button type="button" className="vbi-folder" aria-current={folderFilter === folder.name} onClick={() => setFolderFilter(folder.name)}>
              <AppMark app={folder.app} />
              <span className="vbi-folder-name" title={folder.name}>{folder.name}</span>
              <span className="vbi-folder-count">{chosen}/{folder.keys.length}</span>
            </button>
            <input
              type="checkbox"
              aria-label={`Select all in ${folder.name}`}
              checked={chosen === folder.keys.length && chosen > 0}
              ref={(node) => {
                if (node) node.indeterminate = chosen > 0 && chosen < folder.keys.length;
              }}
              onChange={(event) => toggleKeys(folder.keys.filter((key) => {
                const item = candidates.find((c) => c.key === key);
                return !event.target.checked || item.translation.compatibility.status !== 'unsupported';
              }), event.target.checked)}
              style={{ accentColor: 'var(--vbi-accent)', marginRight: 10 }}
            />
          </div>
        );
      })}
      <h3>Compatibility</h3>
      {STATUS_ORDER.filter((status) => statusCounts[status]).map((status) => (
        <button key={status} type="button" className="vbi-folder" aria-current={statusFilter === status} onClick={() => setStatusFilter(statusFilter === status ? '' : status)}>
          <span className="vbi-folder-name"><CompatBadge status={status} /></span>
          <span className="vbi-folder-count">{statusCounts[status]}</span>
        </button>
      ))}
    </aside>
  );

  const renderLibrarySide = () => (
    <aside className="vbi-side" aria-label="Library folders">
      <h3>Library</h3>
      {[['all', 'All brushes', libraryCount], ['favourites', 'Favourites', records.filter((r) => r.library.favourite && !r.library.deletedAt).length], ['recent', 'Recently used', recentIds.length], ['trash', 'Recently deleted', trashCount]].map(([id, label, count]) => (
        <button key={id} type="button" className="vbi-folder" aria-current={libraryView === id && !libraryFolder} onClick={() => { setLibraryView(id); setLibraryFolder(''); }}>
          <span className="vbi-folder-name">{label}</span>
          <span className="vbi-folder-count">{count}</span>
        </button>
      ))}
      {libraryFolders.length ? <h3>Sets</h3> : null}
      {libraryFolders.map(([folder, count]) => (
        <button key={folder} type="button" className="vbi-folder" aria-current={libraryFolder === folder} onClick={() => { setLibraryView('all'); setLibraryFolder(folder); }}>
          <span className="vbi-folder-name" title={folder}>{folder}</span>
          <span className="vbi-folder-count">{count}</span>
        </button>
      ))}
      {usage ? (
        <div className="vbi-meter" title="Brush images stored in this browser">
          <span>{formatBytes(usage.library)} of brush data · stored on this device</span>
          <div className="vbi-meter-track"><span style={{ width: `${Math.min(100, (usage.library / LIBRARY_SOFT_LIMIT_BYTES) * 100)}%` }} /></div>
        </div>
      ) : null}
    </aside>
  );

  const renderImportMain = () => {
    if (phase === 'idle' && !candidates.length) {
      return (
        <div className="vbi-grid-wrap">
          {resultMessage ? <div className="vbi-notice" data-tone={resultMessage.tone} style={{ marginBottom: 14 }}>{resultMessage.text}</div> : null}
          <div className="vbi-drop" data-active={dragDepth > 0}>
            <DropArt />
            <h3>Drop brush packs here</h3>
            <p>Procreate sets and libraries, Krita bundles and presets, MyPaint, Photoshop ABR and GIMP brushes. Folders and names are kept, and each brush is checked before anything is added.</p>
            <div className="vbi-formats">
              {[['procreate', '.brushset · .brush · .brushlibrary'], ['krita', '.bundle · .kpp'], ['mypaint', '.myb'], ['photoshop', '.abr'], ['gimp', '.gbr · .gih'], ['image', 'PNG tips · .zip']].map(([app, label]) => (
                <span key={app} className="vbi-format"><AppMark app={app} />{label}</span>
              ))}
            </div>
            <button type="button" className="vbi-button" data-variant="primary" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
              <Icon name="upload" />Choose files
            </button>
            <span className="vbi-private">Brushes stay private on this device. Nothing is uploaded.</span>
          </div>
        </div>
      );
    }
    return (
      <div className="vbi-main">
        <div className="vbi-toolbar">
          <strong style={{ fontSize: 14 }}>{folderFilter || (folders.length === 1 ? folders[0].name : `${plural(folders.length, 'pack')}`)}</strong>
          <span style={{ color: 'var(--vbi-text-3)' }}>{plural(visibleCandidates.length, 'brush', 'brushes')}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="vbi-chip" onClick={() => toggleKeys(visibleCandidates.filter((item) => item.translation.compatibility.status !== 'unsupported').map((item) => item.key), true)}>Select all</button>
          <button type="button" className="vbi-chip" onClick={() => toggleKeys(visibleCandidates.map((item) => item.key), false)}>Select none</button>
          <button type="button" className="vbi-chip" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={busy}>Add files</button>
          <button type="button" className="vbi-chip" onClick={clearReview} disabled={busy}>Start over</button>
        </div>
        <div className="vbi-grid-wrap">
          {resultMessage ? <div className="vbi-notice" data-tone={resultMessage.tone} style={{ marginBottom: 12 }}>{resultMessage.text}</div> : null}
          {failures.length ? (
            <div className="vbi-notice" style={{ marginBottom: 12 }}>
              <div><strong>{plural(failures.length, 'file')} could not be used</strong><ul>{failures.slice(0, 6).map((failure, index) => <li key={index}>{failure.fileName}: {failure.message}</li>)}</ul></div>
            </div>
          ) : null}
          {phase === 'inspecting' && !candidates.length ? (
            <div className="vbi-empty"><div className="vbi-spinner" /><strong>Inspecting packs…</strong><span>Archives are checked for unsafe paths and size limits first.</span></div>
          ) : (
            <div className="vbi-grid">
              {visibleCandidates.map((item) => {
                const status = item.translation.compatibility.status;
                const checked = selected.has(item.key);
                const duplicate = duplicates.get(item.key);
                return (
                  <div
                    key={item.key}
                    className="vbi-card"
                    role="button"
                    tabIndex={0}
                    data-focused={focusedKey === item.key}
                    data-muted={status === 'unsupported'}
                    onClick={() => { setFocusedKey(item.key); setDetailOpen(true); }}
                    onKeyDown={(event) => {
                      if (event.key === ' ') {
                        event.preventDefault();
                        toggleKeys([item.key], !checked);
                      } else if (event.key === 'Enter') {
                        setFocusedKey(item.key);
                        setDetailOpen(true);
                      }
                    }}
                  >
                    <span
                      className="vbi-check"
                      role="checkbox"
                      aria-checked={checked}
                      aria-label={`Import ${item.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleKeys([item.key], !checked);
                      }}
                    >
                      <Icon name="check" />
                    </span>
                    <CandidatePreview candidate={item} />
                    {duplicate ? <span className="vbi-flag">In library</span> : null}
                    <div className="vbi-card-body">
                      <span className="vbi-card-name" title={item.name}>{item.name}</span>
                      <span className="vbi-card-meta"><AppMark app={item.app} /><CompatBadge status={status} /></span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {inspectWarnings.length ? (
            <details className="vbi-section" style={{ marginTop: 16 }}>
              <summary>{plural(inspectWarnings.length, 'note')} from inspection</summary>
              <div className="vbi-section-body"><ul>{inspectWarnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>
            </details>
          ) : null}
        </div>
      </div>
    );
  };

  const renderLibraryMain = () => (
    <div className="vbi-main">
      <div className="vbi-toolbar">
        <input className="vbi-search" type="search" placeholder="Search by name, set, app or engine" value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Search brushes" />
        {['', 'procreate', 'krita', 'mypaint', 'photoshop', 'gimp'].filter((app) => !app || records.some((record) => record.source.app === app)).map((app) => (
          <button key={app || 'all'} type="button" className="vbi-chip" aria-pressed={libraryApp === app} onClick={() => setLibraryApp(app)}>{app ? APP_LABELS[app] : 'Any app'}</button>
        ))}
      </div>
      <div className="vbi-grid-wrap">
        {resultMessage && tab === 'library' ? (
          <div className="vbi-notice" data-tone={resultMessage.tone} style={{ marginBottom: 12 }}>
            <div>{resultMessage.text}{resultMessage.failed && resultMessage.failed.length ? <ul>{resultMessage.failed.slice(0, 6).map((item, index) => <li key={index}>{item.name}: {item.message}</li>)}</ul> : null}</div>
          </div>
        ) : null}
        {visibleRecords.length ? (
          <div className="vbi-grid">
            {visibleRecords.map((record) => {
              const shell = shells.get(record.id);
              const checked = librarySelected.has(record.id);
              const paintable = shell ? shell.paintable : true;
              return (
                <div
                  key={record.id}
                  className="vbi-card"
                  role="button"
                  tabIndex={0}
                  data-focused={focusedId === record.id}
                  data-muted={!paintable}
                  onClick={() => { setFocusedId(record.id); setDetailOpen(true); }}
                  onDoubleClick={() => paintable && activateBrush(record)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') activateBrush(record);
                    if (event.key === ' ') {
                      event.preventDefault();
                      setLibrarySelected((current) => {
                        const next = new Set(current);
                        if (next.has(record.id)) next.delete(record.id);
                        else next.add(record.id);
                        return next;
                      });
                    }
                  }}
                >
                  <span
                    className="vbi-check"
                    role="checkbox"
                    aria-checked={checked}
                    aria-label={`Select ${record.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setLibrarySelected((current) => {
                        const next = new Set(current);
                        if (next.has(record.id)) next.delete(record.id);
                        else next.add(record.id);
                        return next;
                      });
                    }}
                  >
                    <Icon name="check" />
                  </span>
                  {libraryView !== 'trash' ? (
                    <button type="button" className="vbi-star" aria-pressed={record.library.favourite} aria-label={record.library.favourite ? 'Remove from favourites' : 'Add to favourites'} onClick={(event) => { event.stopPropagation(); toggleFavourite(record); }}>
                      <Icon name="star" />
                    </button>
                  ) : null}
                  <div className="vbi-card-preview">
                    {shell && shell.previewUrl ? <img src={shell.previewUrl} alt="" /> : <div className="vbi-unpaintable">{paintable ? 'No preview' : 'Source kept · not paintable yet'}</div>}
                  </div>
                  {activeImportedBrushId === record.id ? <span className="vbi-flag">In use</span> : null}
                  <div className="vbi-card-body">
                    <span className="vbi-card-name" title={record.name}>{record.name}</span>
                    <span className="vbi-card-meta"><AppMark app={record.source.app} /><span>{record.library.folder}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="vbi-empty">
            <strong>{libraryView === 'trash' ? 'Nothing recently deleted' : search ? 'No brushes match your search' : 'Your library is empty'}</strong>
            <span>{libraryView === 'trash' ? 'Deleted brushes wait here until you remove them for good.' : 'Import a Procreate, Krita, MyPaint or Photoshop pack to get started.'}</span>
            {!search && libraryView !== 'trash' ? <button type="button" className="vbi-button" data-variant="primary" onClick={() => setTab('import')}>Import brushes</button> : null}
          </div>
        )}
      </div>
    </div>
  );

  const renderDetail = () => {
    if (tab === 'import') {
      if (!focusedCandidate) {
        return <aside className="vbi-detail" data-open={detailOpen}><div className="vbi-empty"><strong>Select a brush</strong><span>Its compatibility, source and a test canvas appear here.</span></div></aside>;
      }
      const status = focusedCandidate.translation.compatibility.status;
      const duplicate = duplicates.get(focusedCandidate.key);
      return (
        <aside className="vbi-detail" data-open={detailOpen} aria-label="Brush details">
          <div className="vbi-detail-head">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <h3 style={{ flex: 1 }}>{focusedCandidate.name}</h3>
              <button type="button" className="vbi-icon-button vbi-detail-close" onClick={() => setDetailOpen(false)} aria-label="Close details"><Icon name="close" /></button>
            </div>
            <div className="vbi-detail-sub"><AppMark app={focusedCandidate.app} /><span>{focusedCandidate.engineLabel}</span><span>·</span><span>{focusedCandidate.folderPath}</span></div>
          </div>
          {duplicate ? <div className="vbi-notice" data-tone="info">Already in your library as “{duplicate.name}” (identical content).</div> : null}
          {status !== 'unsupported' ? <BrushTestCanvas key={focusedCandidate.key} brush={focusedRuntime} /> : (
            <div className="vbi-notice">This brush uses Krita's {focusedCandidate.engineLabel} engine, which Voxemera cannot paint faithfully yet. You can still keep it in your library; the original preset is stored for a future engine.</div>
          )}
          <CompatibilityDetails compatibility={focusedCandidate.translation.compatibility} />
          <SourceDetails source={focusedCandidate.source} engineLabel={focusedCandidate.engineLabel} />
        </aside>
      );
    }
    if (!focusedRecord) {
      return <aside className="vbi-detail" data-open={detailOpen}><div className="vbi-empty"><strong>Select a brush</strong><span>Rename, adjust, duplicate or export it here.</span></div></aside>;
    }
    const deleted = !!focusedRecord.library.deletedAt;
    const paintable = focusedLibraryRuntime ? focusedLibraryRuntime.paintable : true;
    return (
      <aside className="vbi-detail" data-open={detailOpen} aria-label="Brush details">
        <div className="vbi-detail-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input className="vbi-name-input" value={nameDraft} onChange={(event) => setNameDraft(event.target.value)} onBlur={commitName} onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()} aria-label="Brush name" disabled={deleted} />
            <button type="button" className="vbi-icon-button vbi-detail-close" onClick={() => setDetailOpen(false)} aria-label="Close details"><Icon name="close" /></button>
          </div>
          <div className="vbi-detail-sub"><AppMark app={focusedRecord.source.app} /><span>{APP_LABELS[focusedRecord.source.app] || focusedRecord.source.app}</span><span>·</span><span>{focusedRecord.library.folder}</span><CompatBadge status={focusedRecord.compatibility.status} /></div>
        </div>
        <div className="vbi-detail-actions">
          {deleted ? (
            <>
              <button type="button" className="vbi-button" onClick={restoreSelected}><Icon name="undo" />Restore</button>
              <button type="button" className="vbi-button" data-variant="danger" onClick={purgeSelected}><Icon name="trash" />Delete for good</button>
            </>
          ) : (
            <>
              <button type="button" className="vbi-button" data-variant="primary" disabled={!paintable} onClick={() => activateBrush(focusedRecord)}>{activeImportedBrushId === focusedRecord.id ? 'In use' : 'Use brush'}</button>
              <button type="button" className="vbi-button" onClick={() => duplicateRecord(focusedRecord)} title="Duplicate"><Icon name="copy" /></button>
              <button type="button" className="vbi-button" onClick={() => exportSelected(false)} title="Export as .voxbrush"><Icon name="download" /></button>
              <button type="button" className="vbi-button" data-variant="danger" onClick={deleteSelected} title="Delete"><Icon name="trash" /></button>
            </>
          )}
        </div>
        {paintable && !deleted ? <BrushTestCanvas key={focusedRecord.id + ':' + focusedRecord.updatedAt} brush={focusedLibraryRuntime} /> : null}
        {paintable && !deleted ? <BrushAdjustments record={focusedRecord} onChange={adjustRecord} /> : null}
        <CompatibilityDetails compatibility={focusedRecord.compatibility} defaultOpen={false} />
        <SourceDetails source={focusedRecord.source} />
      </aside>
    );
  };

  const renderFooter = () => {
    if (progress) {
      return (
        <footer className="vbi-footer">
          <div className="vbi-progress">
            <div className="vbi-progress-track"><span style={{ width: `${Math.round(Math.min(1, progress.value || 0) * 100)}%` }} /></div>
            <span className="vbi-progress-label">{progress.label}</span>
          </div>
          <button type="button" className="vbi-button" onClick={cancel}>Cancel</button>
        </footer>
      );
    }
    if (tab === 'import') {
      return (
        <footer className="vbi-footer">
          <div className="vbi-footer-info">
            {candidates.length ? <><strong>{selectedCandidates.length}</strong> of {plural(candidates.length, 'brush', 'brushes')} selected</> : 'Imported brushes are added to your library on this device.'}
          </div>
          {selectedDuplicates ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--vbi-text-2)', fontSize: 12 }}>
              {plural(selectedDuplicates, 'duplicate')}
              <select className="vbi-select" value={duplicatePolicy} onChange={(event) => setDuplicatePolicy(event.target.value)}>
                <option value="skip">Skip</option>
                <option value="replace">Replace</option>
                <option value="keep">Keep both</option>
              </select>
            </label>
          ) : null}
          <button type="button" className="vbi-button" data-variant="primary" disabled={!selectedCandidates.length || busy} onClick={runImport}>
            {selectedCandidates.length ? `Import ${plural(selectedCandidates.length, 'brush', 'brushes')}` : 'Import'}
          </button>
        </footer>
      );
    }
    const count = librarySelected.size;
    return (
      <footer className="vbi-footer">
        <div className="vbi-footer-info">{count ? <><strong>{count}</strong> selected</> : `${plural(libraryCount, 'brush', 'brushes')} in your library`}</div>
        {count ? (
          libraryView === 'trash' ? (
            <>
              <button type="button" className="vbi-button" onClick={restoreSelected}><Icon name="undo" />Restore</button>
              <button type="button" className="vbi-button" data-variant="danger" onClick={purgeSelected}>Delete for good</button>
            </>
          ) : (
            <>
              <button type="button" className="vbi-button" onClick={() => exportSelected(false)}><Icon name="download" />Export</button>
              <button type="button" className="vbi-button" data-variant="danger" onClick={deleteSelected}><Icon name="trash" />Delete</button>
              <button type="button" className="vbi-button" data-variant="ghost" onClick={() => setLibrarySelected(new Set())}>Clear</button>
            </>
          )
        ) : (
          <>
            <button type="button" className="vbi-button" onClick={() => restoreInputRef.current && restoreInputRef.current.click()}>Restore backup</button>
            <button type="button" className="vbi-button" disabled={!libraryCount} onClick={() => exportSelected(true)}><Icon name="download" />Back up library</button>
          </>
        )}
      </footer>
    );
  };

  // Portalled to <body>: the panels that open it can sit inside transformed
  // or hidden drawers, which would trap a position: fixed dialog.
  return createPortal(
    <div
      className="vbi-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy && typeof onClose === 'function') onClose();
      }}
    >
      <div
        className="vbi-root"
        role="dialog"
        aria-modal="true"
        aria-label="Brush library"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <input ref={fileInputRef} type="file" accept={ACCEPT} multiple hidden onChange={(event) => { startInspection(event.target.files); event.target.value = ''; }} />
        <input ref={restoreInputRef} type="file" accept=".voxbrush,.zip" hidden onChange={(event) => { startInspection(event.target.files); event.target.value = ''; }} />
        <header className="vbi-header">
          <div className="vbi-title">
            <h2>Brushes</h2>
            <p>Import Procreate, Krita, MyPaint and Photoshop brushes · stored privately on this device</p>
          </div>
          <div className="vbi-tabs" role="tablist" aria-label="Brush library sections">
            <button type="button" className="vbi-tab" role="tab" aria-selected={tab === 'import'} onClick={() => setTab('import')}>
              Import{candidates.length ? <span className="vbi-count">{candidates.length}</span> : null}
            </button>
            <button type="button" className="vbi-tab" role="tab" aria-selected={tab === 'library'} onClick={() => setTab('library')}>
              Library<span className="vbi-count">{libraryCount}</span>
            </button>
          </div>
          <button type="button" className="vbi-icon-button" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </header>
        <div className="vbi-body" style={tab === 'import' && phase === 'idle' && !candidates.length ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
          {tab === 'import' ? (phase === 'idle' && !candidates.length ? null : renderImportSide()) : renderLibrarySide()}
          {tab === 'import' ? renderImportMain() : renderLibraryMain()}
          {tab === 'import' && phase === 'idle' && !candidates.length ? null : renderDetail()}
        </div>
        {renderFooter()}
        {dragDepth > 0 && (tab !== 'import' || candidates.length) ? <div className="vbi-drop-overlay">Drop to inspect brushes</div> : null}
      </div>
    </div>,
    document.body,
  );
}
